import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import CircularProgress from 'apollo-react/components/CircularProgress';
import DataTableComponent from '../../common/DataTable/PrimeDataTable';
import axiosInstance from '../../../axiosInstance';
import { convertUTCToLocal } from '../../utils/dateUtils';
// No longer need mapping - routes directly use API resource names

const UserLogs = ({ persona }) => {
  const { t, ready } = useTranslation();
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [paginationParams, setPaginationParams] = useState({
    limit: 10,
    offset: 0,
    sortField: 'createdAt',
    sortOrder: -1,
    filters: {
      global: { value: null, matchMode: 'contains' },
      entity_name: { value: null, matchMode: 'contains' },
      actor: { value: null, matchMode: 'contains' },
      action: { value: null, matchMode: 'contains' },
      target: { value: null, matchMode: 'contains' },
      resource: { value: null, matchMode: 'contains' },
      entity_id: { value: null, matchMode: 'contains' },
      createdAt: { value: null, matchMode: 'contains' },
    },
  });
  const location = useLocation();

  // Extract resource from the URL path (e.g., /admin/logs/role-groups -> role-groups)
  const pathParts = location.pathname.split('/');
  const apiResource = pathParts[pathParts.length - 1]; // Get the last part of the path

  if (!ready) {
    return (
      <Grid container spacing={2} sx={{ width: '100%' }}>
        <Grid size={12}>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <CircularProgress />
          </div>
        </Grid>
      </Grid>
    );
  }

  const singularAndCapitalize = (str) =>
    str
      ? (str.endsWith('s') ? str.slice(0, -1) : str).replace(/^./, (c) =>
          c.toUpperCase()
        )
      : '';

  useEffect(() => {
    setLoading(true);
    const fetchLogs = async () => {
      try {
        const response = await axiosInstance
          .get(
            // eslint-disable-next-line no-undef
            `${process.env.REACT_APP_SERVER_URL}/api/logs/${apiResource}`,
            {
              withCredentials: true,
              params: paginationParams,
            }
          )
          .catch((err) => {
            console.error('Error fetching logs:', err);
            throw err; // Re-throw to handle in outer catch
          });

        setData(response.data.data);
        setTotalRecords(response.data.total || 0);
      } catch (error) {
        console.error('Failed to fetch logs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [apiResource, paginationParams, persona]);

  const onDataTableEvent = (event) => {
    setPaginationParams((prev) => ({
      ...prev,
      limit: event.rows,
      offset: event.first,
      sortField: event.sortField || prev.sortField,
      sortOrder: event.sortOrder || prev.sortOrder,
      // filters: event.filters || prev.filters,
    }));
  };

  const columns = [
    { field: 'id', header: t('ID'), sortable: true, filter: true },
    ...(apiResource === 'other'
      ? [
          {
            field: 'entity_id',
            header: t('Entity ID'),
            sortable: true,
            filter: true,
          },
        ]
      : []),
    ...(apiResource === 'org-structure'
      ? [
          {
            field: 'organization_name',
            header: t('Organization Name'),
            sortable: true,
            filter: true,
          },
          {
            field: 'department_name',
            header: t('Department Name'),
            sortable: true,
            filter: true,
          },
        ]
      : [
          {
            field: 'entity_name',
            header: t(
              apiResource === 'other'
                ? 'Entity Name'
                : `${singularAndCapitalize(apiResource)}  Name`
            ),
            sortable: true,
            filter: true,
          },
        ]),
    { field: 'actor', header: t('Actor'), sortable: true, filter: true },
    ...(apiResource === 'other'
      ? [
          {
            field: 'resource',
            header: t('Resource'),
            sortable: true,
            filter: true,
          },
        ]
      : []),
    ...(apiResource === 'admin-users'
      ? [
          {
            field: 'target',
            header: t('Target'),
            sortable: true,
            filter: true,
          },
        ]
      : []),
    { field: 'action', header: t('Action'), sortable: true, filter: true },
    {
      field: 'createdAt',
      header: t('Created at'),
      sortable: true,
      filter: true,
    },
  ];

  const actions = [
    {
      text: t('view'),
      color: 'primary',
      onClick: (row) =>
        navigate(`/${persona}/logs/${apiResource}/log/${row.id}`),
    },
  ];

  const body = data.map((log) => ({
    id: log.id,
    ...(apiResource === 'other' ? { entity_id: log.entity_id } : {}),
    ...(apiResource === 'org-structure'
      ? {
          organization_name: log.organization_name || 'N/A',
          department_name: log.department_name || 'N/A',
        }
      : { entity_name: log.entity_name }),
    actor: `${log.actor_username} (${log.actor_email})`,
    ...(apiResource === 'admin-users'
      ? {
          target: `${log.target_username ? log.target_username : 'N/A'} (${log.target_email ? log.target_email : 'N/A'})`,
        }
      : {}),
    action: log.action,
    ...(apiResource === 'other' ? { resource: log.resource } : {}),
    createdAt: convertUTCToLocal(log.createdAt),
  }));

  return (
    <div>
      <DataTableComponent
        data={body}
        columns={columns}
        loading={loading}
        paginationParams={paginationParams}
        totalRecords={totalRecords}
        onDataTableEvent={onDataTableEvent}
        enableCreate={false}
        enablePagination={true}
        enableLazyLoading={true}
        enableExport={true}
        actions={actions}
        sectionName={t('logs')}
      />
    </div>
  );
};

export default UserLogs;
